import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/apiClient';
import { usuariosService } from '@/features/usuarios/services/usuariosService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import logo from '@/assets/logo.jpg';

const passwordSchema = z.object({
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

export default function ResetPassword() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const pwd = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    try {
      passwordSchema.parse({ password: pwd, confirmPassword });

      if (!user) {
        toast({
          title: 'Sessão expirada',
          description: 'Faça login novamente para redefinir sua senha.',
          variant: 'destructive',
        });
        navigate('/auth');
        return;
      }

      await usuariosService.resetSenha(user.id, { novaSenha: pwd });
      toast({ title: 'Senha atualizada!', description: 'Sua senha foi redefinida com sucesso.' });
      navigate('/');
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: 'Dados inválidos', description: error.errors[0].message, variant: 'destructive' });
      } else {
        toast({
          title: 'Erro',
          description: getApiErrorMessage(error, 'Não foi possível redefinir a senha.'),
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[hsl(222,40%,10%)]">
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,40%,12%)] via-[hsl(220,35%,14%)] to-[hsl(222,42%,8%)]" />
      <div className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[100px] animate-[pulse_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] rounded-full bg-secondary/10 blur-[100px] animate-[pulse_8s_ease-in-out_infinite_1s]" />

      <div className="w-full max-w-md animate-fade-in relative z-10">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-5">
            <div className="absolute inset-0 rounded-2xl bg-primary/20 blur-xl scale-150 animate-[pulse_4s_ease-in-out_infinite]" />
            <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl overflow-hidden shadow-strong hover-scale cursor-pointer ring-2 ring-primary/20 ring-offset-2 ring-offset-[hsl(222,40%,12%)]">
              <img src={logo} alt="Udisys Gestor" className="w-20 h-20 object-cover" />
            </div>
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-white">Redefinir Senha</h1>
          <p className="text-sm text-[hsl(220,15%,55%)] mt-1.5">Digite sua nova senha abaixo</p>
        </div>

        <Card className="shadow-strong border border-[hsl(222,25%,20%)]/50 bg-[hsl(222,30%,14%)]/80 backdrop-blur-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-heading text-center text-white">Nova senha</CardTitle>
            <CardDescription className="text-center text-[hsl(220,15%,55%)]">
              Escolha uma senha segura com no mínimo 6 caracteres
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[hsl(220,15%,75%)]">Nova senha</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••"
                  required
                  disabled={loading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[hsl(222,30%,18%)] border-[hsl(222,25%,24%)] text-white placeholder:text-[hsl(220,15%,40%)] focus:border-primary/50 focus:ring-primary/20"
                />
                <PasswordStrengthIndicator password={password} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[hsl(220,15%,75%)]">Confirmar senha</Label>
                <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="••••••" required disabled={loading} className="bg-[hsl(222,30%,18%)] border-[hsl(222,25%,24%)] text-white placeholder:text-[hsl(220,15%,40%)] focus:border-primary/50 focus:ring-primary/20" />
              </div>
              <Button type="submit" className="w-full bg-gradient-primary hover:opacity-90 transition-all duration-200 shadow-[0_4px_15px_hsl(220,72%,42%,0.3)] hover:shadow-[0_6px_20px_hsl(220,72%,42%,0.4)]" disabled={loading}>
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </span>
                ) : 'Salvar nova senha'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-xs text-[hsl(220,15%,40%)] text-center mt-6">
          © 2026 Udisys Gestor · Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
